import { useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { usePlatform } from '@localagent/platform';
import {
  clearAuthAction,
  isDelegatedSessionToken,
  stytchSessionJwtAtom,
  stytchSessionTokenAtom,
} from '../stores/auth/atoms';
import { getRegisteredStytchClient } from '../auth/StytchSessionAuthProvider';
import { getSDK } from '../services/sdk-instance';
import { useShogunchop } from '../providers/ShogunChopProvider';

export function useSignOut(): { signOut: () => Promise<void>; isSigningOut: boolean } {
  const platform = usePlatform();
  const stytchSessionToken = useAtomValue(stytchSessionTokenAtom);
  const stytchSessionJwt = useAtomValue(stytchSessionJwtAtom);
  const clearAuth = useSetAtom(clearAuthAction);
  const shogunchop = useShogunchop();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const signOut = async () => {
    if (isSigningOut) {
      return;
    }
    setIsSigningOut(true);
    try {
      if (platform.type === 'desktop' && shogunchop.resource) {
        // Desktop: daemon is the auth authority (la ADR-013…016).
        await shogunchop.resource.logout();
      } else {
        try {
          await getSDK().auth.revokeSession({
            session_token: isDelegatedSessionToken(stytchSessionToken ?? '')
              ? null
              : stytchSessionToken,
            session_jwt: stytchSessionJwt,
          });
        } catch (error) {
          console.error('Failed to revoke Stytch session during logout:', error);
        }
        const stytchClient = getRegisteredStytchClient();
        if (!stytchClient) {
          console.error(
            'logout: no registered Stytch client — Stytch cookies cannot be cleared, session may resurrect',
          );
        }
        try {
          await stytchClient?.session.revoke();
        } catch (error) {
          console.error('Failed to clear Stytch session cookies during logout:', error);
        }
        await clearAuth('user_signout');
      }
    } finally {
      setIsSigningOut(false);
    }
  };

  return { signOut, isSigningOut };
}
